/**
 * Sentry Configuration for Better Stack
 *
 * Called as the very first import in main.tsx.
 * Initializes Sentry SDK pointed at Better Stack's Sentry-compatible endpoint.
 * No-op when VITE_SENTRY_DSN is not set (local dev).
 */

import * as Sentry from '@sentry/react'

/** Detect staging vs production from the hostname */
function detectEnvironment(): string {
  const host = window.location.hostname
  if (host.includes('staging') || host.includes('develop') || host.endsWith('.workers.dev')) {
    return 'staging'
  }
  if (host === 'piccard.app' || host === 'www.piccard.app') {
    return 'production'
  }
  if (host.includes('pages.dev')) {
    return 'preview'
  }
  return 'production'
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

  if (!dsn) {
    return
  }

  const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined
  // Use explicit VITE_SENTRY_ENVIRONMENT if set, otherwise detect from URL
  const explicitEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined
  const environment = explicitEnv || detectEnvironment()

  Sentry.init({
    dsn,
    environment,
    release: appVersion || undefined,
    sendDefaultPii: false,
    normalizeDepth: 5,
    tracesSampleRate: 0.2,
    integrations: [],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      /chrome-extension/,
      /moz-extension/,
    ],
    allowUrls: [
      /piccard\.app/,
      window.location.origin,
    ],
  })
}
